// src/lib/brand-config.js
//
// SINGLE SOURCE OF BRAND + DOMAIN + SENDER CONSTANTS.
//
// B1/B1c centralised every visible brand literal onto BRAND.name. B2a switched
// the brand NAME to HeliGreece from this single point. B2b (this wave) flips the
// DOMAIN: host + all sender addresses + the thread-relay domain move to
// heligreece.com. The OLD domain stays on the Vercel project and 308-redirects
// to the new host (vercel.json), /api/* excepted (live Mollie + the account-wide
// Resend inbound webhook sit on the old domain). NOTE the actual email From-header
// comes from the RESEND_FROM env var, not BRAND.noreplyEmail (that is only the
// fallback) — the owner must update RESEND_FROM/SITE_URL in Vercel.
//
// PRECEDENCE: where a value is also configurable in the CMS (siteSettings), the
// CMS still wins — brandName(cmsSiteName) returns the CMS value when present and
// falls back to BRAND.name. IMPORTANT after this flip: the CMS
// siteSettings.siteName is still 'GreekHelicopters' in production and WINS over
// this default on the surfaces that read it (jsonld/og/meta titles), so the owner
// must set siteSettings.siteName = 'HeliGreece' in the Studio for those surfaces
// to show the new name. Surfaces that use BRAND.name directly (emails, logo,
// page copy) flip immediately.
//
// The admin logo (src/lib/admin/branding.js) is a separate, already-shipped
// surface and is NOT sourced from here.

export const BRAND = {
  // Brand name — the default behind every `siteSettings.siteName || 'GreekHelicopters'`
  // fallback (jsonld.js:119/189/199/519/531/580, seo-meta.js:67, index.astro:118,
  // App.jsx:7112, rss.xml.js:22) and the hardcoded Mollie descriptions
  // (override-price.js:99, resend-payment.js:59). Also the email sender display
  // name (resend.js:53 + per-call fromName in _helpers.js/actions.js).
  name: 'HeliGreece',

  // The email/page wordmark (src/lib/brand.js logoHtml) renders the name as a
  // two-tone split — dark first part + accent second part. Model that split here
  // so the wordmark is generated FROM the name (byte-identical today: the two
  // parts concatenate to name). A rebrand sets the split for the new name; a
  // one-element array (or a name with no natural split) renders as a single tone.
  wordmarkParts: ['Heli', 'Greece'],

  // Legal company — stays Astraios Aviation SMPC on a rebrand (only the brand
  // changes). Source: brand.js:47/54/74 imprint, jsonld.js legalName, CMS
  // siteSettings.companyName (schema:1379).
  legalName: 'Astraios Aviation SMPC',

  // Canonical public host (no scheme). B2b cutover → www.heligreece.com; it
  // matches astro.config.mjs `site` and preview-indexing PROD_ORIGIN and is the
  // SINGLE host the unset-SITE_URL fallback resolves to. The OLD host
  // (www.greekhelicopters.com) stays on the Vercel project and 308-redirects to
  // this one (vercel.json), and /api/* is intentionally NOT redirected so live
  // Mollie webhook/redirect URLs on the old domain keep working.
  host: 'www.heligreece.com',

  // Email addresses. B2b: all @heligreece.com (Resend-verified). The old domain
  // still receives (kept on the project); the thread relay accepts BOTH reply
  // domains during the transition (see replyDomain + inbound/email.js).
  supportEmail: 'info@heligreece.com',      // resend.js CONTACT_EMAIL (footers + Reply-To target)
  charterEmail: 'charter@heligreece.com',   // CMS siteSettings.charterEmail default; data-loader.js, App.jsx
  invoiceEmail: 'invoices@heligreece.com',  // _helpers.js operatorFooter, operator/terms.js
  opsEmail: 'ops@heligreece.com',           // booking/[ref]/email-customer.js sender, operator/confirm.js
  privacyEmail: 'privacy@heligreece.com',   // components/legal/LegalPages.jsx
  noreplyEmail: 'no-reply@heligreece.com',  // resend.js from-address fallback

  // Inbound/Reply thread-relay domain (masked per-booking address
  // b-<token>@<replyDomain>). B2b → reply.heligreece.com (Resend-verified). The
  // inbound webhook (src/pages/api/inbound/email.js) accepts BOTH this and the
  // legacy reply.greekhelicopters.com so already-sent Reply-To addresses on
  // in-flight bookings keep threading during the transition.
  replyDomain: 'reply.heligreece.com',

  // Email sender display name — resend.js:53 `GreekHelicopters <...>` + ~12
  // per-call fromName. Kept equal to name; a rebrand changes both via `name`.
  get senderName() { return this.name; },

  // Mollie customer-visible payment description brand prefix (override-price.js:99,
  // resend-payment.js:59). NOTE the live Mollie STATEMENT descriptor is set in the
  // Mollie dashboard, not in code — this only prefixes the checkout `description`.
  get statementDescriptor() { return this.name; },

  // OG / share image. Today CMS-driven (settings.ogImage → homepage heroImage
  // fallback, seo-meta.js:70-73). App.jsx:7117 falls back to
  // `https://www.greekhelicopters.com/logo.png` — WHICH DOES NOT EXIST in public/
  // (broken URL, flagged in B0 discovery). Modelled here as ogFallbackPath so the
  // broken reference has one home; NO new asset is invented in B1.
  ogFallbackPath: '/logo.png',

  // Static brand icons in public/ (regenerate list for a rebrand). Not wired to a
  // web manifest today (none exists); android-chrome-*.png are orphaned.
  favicons: {
    ico: '/favicon.ico',
    svg: '/favicon.svg',
    appleTouch: '/apple-touch-icon.png',
  },
};

// Canonical base URL. Returns SITE_URL when set (production → byte-identical to
// today), else PUBLIC_SITE_URL, else the ONE canonical host — collapsing the old
// apex / www / vercel.app fallback drift into a single derivation. No trailing
// slash. Works under Astro/Vite SSR + vitest (import.meta.env) and plain Node
// scripts (process.env) via the guarded reads below.
export function siteBaseUrl() {
  // The EXACT `import.meta.env.SITE_URL` form is what Vite statically inlines at
  // build (an aliased read would not be replaced) — so this resolves identically
  // to the inline fallbacks it replaces. try/catch guards plain-Node scripts
  // where import.meta.env is undefined; those fall through to process.env.
  let site = '';
  try { site = import.meta.env.SITE_URL || import.meta.env.PUBLIC_SITE_URL || ''; } catch { /* not a Vite context */ }
  if (!site && typeof process !== 'undefined' && process.env) {
    site = process.env.SITE_URL || process.env.PUBLIC_SITE_URL || '';
  }
  const url = site || ('https://' + BRAND.host);
  return url.replace(/\/+$/, '');
}

// Brand-name precedence helper: CMS siteName wins, BRAND.name is the default
// (replaces the inline `|| 'GreekHelicopters'` literals). Identical output today.
export function brandName(cmsSiteName) {
  const s = (typeof cmsSiteName === 'string') ? cmsSiteName.trim() : '';
  return s || BRAND.name;
}
